import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import { api, ApiError, type Me } from './lib/api';

interface SessionState {
  /** undefined = still loading; null = signed out */
  me: Me | null | undefined;
  error: string | null;
  refresh: () => Promise<Me | null>;
  setMe: (m: Me | null) => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState>(null as unknown as SessionState);

export function SessionProvider({ children }: { children: ComponentChildren }) {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const m = await api<Me>('/v1/me');
      setMe(m); setError(null);
      return m;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { setMe(null); setError(null); return null; }
      setError((e as Error).message);
      setMe((prev) => (prev === undefined ? null : prev));
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
    const onUnauth = () => setMe(null);
    window.addEventListener('ayx:unauthenticated', onUnauth);
    return () => window.removeEventListener('ayx:unauthenticated', onUnauth);
  }, [refresh]);

  const signOut = useCallback(async () => {
    await api('/v1/auth/logout', { method: 'POST' }).catch(() => {});
    setMe(null);
  }, []);

  return <Ctx.Provider value={{ me, error, refresh, setMe, signOut }}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);

/** Validate a post-auth redirect target: same-site path or a checkout intent. */
export function safeNext(next: string | undefined | null): string | null {
  if (!next) return null;
  if (/^checkout:((pro|studio):)?(month|year)$/.test(next)) return next;
  if (next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/\\')) return next;
  return null;
}
